import React, { useCallback, useEffect, useState } from 'react';
import { Banknote, Undo2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { oapi } from '@/lib/officialsApi';
import { PayFilters } from './PayFilters';
import { Loading, METHOD_LABEL, Money, Panel, ReasonDialog } from './PayBits';

const PaymentRow = ({ p, currency, canReverse, onReverse, openOfficial }) => {
  const [open, setOpen] = useState(false);
  const reversed = p.status === 'reversed';
  return (
    <div className="border-t first:border-t-0" data-testid={`payment-row-${p.id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
        <div className="w-24 text-xs font-semibold text-slate-500">{p.paid_on}</div>
        <div className="flex-1 min-w-[180px]">
          <button type="button" onClick={() => openOfficial(p.official_id)} className="font-semibold text-[#00247D] hover:underline text-left">{p.official_name}</button>
          <div className="text-xs text-slate-500">{METHOD_LABEL[p.method] || p.method}{p.reference ? ` · Ref ${p.reference}` : ''} · {p.games_covered} game{p.games_covered === 1 ? '' : 's'} · by {p.recorded_by_name}</div>
        </div>
        {reversed && <span className="text-[11px] font-bold text-red-700 bg-red-50 rounded-full px-2 py-0.5" data-testid={`payment-reversed-${p.id}`}>Reversed</span>}
        <Money minor={p.amount_minor} currency={currency} className={`font-bold ${reversed ? 'line-through text-slate-400' : 'text-emerald-700'}`} testId={`payment-amount-${p.id}`} />
        <button type="button" onClick={() => setOpen(o => !o)} className="p-1 text-slate-400 hover:text-slate-700" data-testid={`payment-details-${p.id}`}>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {canReverse && !reversed && (
          <Button size="sm" variant="ghost" className="h-8 text-red-600 hover:text-red-700" onClick={() => onReverse(p)} data-testid={`payment-reverse-${p.id}`}><Undo2 className="w-4 h-4 mr-1" />Reverse</Button>
        )}
      </div>
      {open && (
        <div className="px-4 pb-3 pl-4 sm:pl-[7.5rem] text-xs text-slate-600 space-y-1" data-testid={`payment-allocations-${p.id}`}>
          {(p.allocations || []).map(a => (
            <div key={a.earning_id} className="flex justify-between gap-3 max-w-xl"><span className="truncate">{a.label}</span><Money minor={a.amount_minor} currency={currency} /></div>
          ))}
          {p.notes && <div className="italic text-slate-500">Note: {p.notes}</div>}
          {reversed && <div className="text-red-600">Reversed by {p.reversed_by_name}: {p.reverse_reason}</div>}
        </div>
      )}
    </div>
  );
};

export const PaymentsHistory = ({ orgId, currency, perms, competitions, officials, roles, tick, refresh, openOfficial, recordPayment }) => {
  const [f, setF] = useState({});
  const [d, setD] = useState(null);
  const [rev, setRev] = useState(null);
  const load = useCallback(() => { oapi('/officials/payments/payments', { orgId, query: f }).then(setD).catch(e => toast.error(e.message)); }, [orgId, f]);
  useEffect(() => { load(); }, [load, tick]);
  const reverse = async ({ reason }) => {
    try {
      await oapi(`/officials/payments/payments/${rev.id}/reverse`, { method: 'POST', orgId, body: { reason } });
      toast.success('Payment reversed · balance restored');
      setRev(null);
      refresh();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="space-y-4" data-testid="pay-history">
      <PayFilters f={f} setF={setF} competitions={competitions} officials={officials} roles={roles} showStatus={false} showRole={false} showLeague={false} />
      <Panel title="Payment history" testId="history-panel"
        desc={d ? `${d.count} recorded payment${d.count === 1 ? '' : 's'} · total ` : ''}
        actions={<div className="flex items-center gap-3">
          {d && <Money minor={d.total_minor} currency={currency} className="font-black text-lg text-emerald-700" testId="history-total" />}
          {perms.has('record') && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => recordPayment({})} data-testid="history-record-btn"><Banknote className="w-4 h-4 mr-1.5" />Record payment</Button>}
        </div>}>
        {!d ? <Loading /> : d.payments.map(p => <PaymentRow key={p.id} p={p} currency={currency} canReverse={perms.has('record')} onReverse={setRev} openOfficial={openOfficial} />)}
        {d && !d.payments.length && <p className="py-10 text-center text-sm text-slate-500" data-testid="history-empty">No payments recorded yet.</p>}
      </Panel>
      <ReasonDialog open={!!rev} title="Reverse this payment?" danger confirmLabel="Reverse payment" testId="history-reverse-dialog"
        description={rev ? `${rev.official_name} · ${rev.paid_on}. The record stays in the history and the amount goes back to outstanding.` : ''}
        onClose={() => setRev(null)} onSubmit={reverse} />
    </div>
  );
};
